import React, { useState, useRef, useEffect } from 'react';
import { Handle, Position, useReactFlow } from 'reactflow';
import { Plus, MoreVertical, Trash2, Edit2, Shuffle } from 'lucide-react';
import { useGetNodeOptionsQuery } from '@frontend/app/hooks';
import {
  ArrayConditionOperators,
  BooleanConditionOperators,
  DateConditionOperators,
  NumberConditionOperators,
  TextConditionOperators,
} from '@frontend/app/types/automations/condition/ConditionOperator';
import {
  ComboOperator,
  ComparisonOperator,
} from '@frontend/app/types/automations/condition/Operator';

import { FieldType } from '@frontend/app/types/globalTypes';
import { upperFirst } from 'lodash';
import moment from 'moment';
import { convertToTitleCase } from '@frontend/app/utils/strings';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/components/ui/popover';
import { Button } from '@/shadcn/components/ui/button';
import { cn } from '@/shadcn/lib/utils';
import { FlowNodeData } from '../types';

interface ConditionNodeV2Props {
  data: FlowNodeData;
}

export const ConditionNodeV2: React.FC<ConditionNodeV2Props> = ({ data }) => {
  const {
    nodeId,
    metadata,
    isEditing,
    isSelected,
    onNodeClick,
    onDeleteNode,
    automationId,
    templateId,
  } = data;

  const [showOptionsPopover, setShowOptionsPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Fetch condition options - match V1 pattern exactly
  const {
    data: {
      getNodeOptions: {
        conditionOptions = [],
      } = { conditionOptions: [] },
    } = { getNodeOptions: { conditionOptions: [] } },
  } = useGetNodeOptionsQuery({
    variables: {
      context: {
        automationId,
        templateId,
      },
    },
    skip: !automationId && !templateId,
    fetchPolicy: 'cache-and-network', // Ensure fresh data
  });

  // Render condition tag with proper formatting - match V1 logic exactly
  const renderConditionTag = (condition: any): string => {
    const operator = condition.mappedOperator || condition.op;
    const field = conditionOptions.find((field) => field.id === condition.attribute);
    if (!field) {
      return condition.attribute ? `Condition: ${condition.attribute}` : 'No added conditions.';
    }
    const fieldType = field.attributeType;
    const fieldDisplayName = field.displayName ? upperFirst(field.displayName) : field.id;

    // Get group display name dynamically based on what's shown in the dropdown
    // This matches how ConditionFormV2 displays groups using convertToTitleCase
    const getGroupDisplayName = (group: string): string => {
      return convertToTitleCase(group.replace(/_/g, ' '));
    };

    const groupName = field.group ? getGroupDisplayName(field.group) : '';
    const displayName = groupName ? `${groupName} ${fieldDisplayName}` : fieldDisplayName;
    const value = condition.value;

    const getDisplayOption = () => {
      switch (fieldType) {
        case FieldType.ANNUAL:
        case FieldType.DATE:
          return DateConditionOperators.find((op) => op.value === operator);
        case FieldType.ARRAY:
          return ArrayConditionOperators.find((op) => op.value === operator);
        case FieldType.BOOLEAN:
          return BooleanConditionOperators.find((op) => op.value === operator);
        case FieldType.NUMBER:
        case FieldType.PERCENTAGE:
          return NumberConditionOperators.find((op) => op.value === operator);
        case FieldType.TEXT:
        case FieldType.EMAIL:
          return TextConditionOperators.find((op) => op.value === operator);
        default:
          return null;
      }
    };

    const displayOption = getDisplayOption();
    const inlineText = displayOption?.inlineLabel || '';
    const secondaryInlineText = displayOption?.secondaryInlineLabel || '';

    if (
      operator === ComboOperator.BETWEEN
      && (
        !Array.isArray(value)
        || value.length !== 2
      )
    ) {
      throw new Error('Invalid value for between operator');
    }

    switch (fieldType) {
      case FieldType.ANNUAL:
      case FieldType.DATE: {
        const dateFormat = fieldType === FieldType.DATE ? 'MM/DD/YYYY' : 'MMM D';
        const valueAsDate = moment(value as Date).utc().format(dateFormat);
        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComboOperator.NOT_EQUALS:
          case ComparisonOperator.GREATER_THAN:
          case ComparisonOperator.GREATER_THAN_EQUALS:
          case ComparisonOperator.LESS_THAN:
          case ComparisonOperator.LESS_THAN_EQUALS:
            return `${displayName} ${inlineText} ${valueAsDate} ${secondaryInlineText}`;
          case ComboOperator.BETWEEN:
            return `${displayName} ${inlineText} ${moment(value[0]).utc().format(dateFormat)} ${secondaryInlineText} ${moment(value[1]).utc().format(dateFormat)}`;
          case ComparisonOperator.HAS_VALUE:
          case ComboOperator.IS_NULL:
            return `${displayName} ${inlineText}`;
          case ComparisonOperator.MORE_THAN_AGO:
          case ComboOperator.NOT_MORE_THAN_AGO:
          case ComparisonOperator.IN_THE_LAST:
          case ComboOperator.NOT_IN_THE_LAST:
          case ComboOperator.NOT_IN_THE_NEXT:
          case ComparisonOperator.IN_THE_NEXT:
            return `${displayName} ${inlineText} ${value} ${secondaryInlineText}`;
          default:
            throw new Error('Invalid comparison operator for date attribute');
        }
      }
      case FieldType.ARRAY: {
        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComboOperator.NOT_EQUALS:
          case ComparisonOperator.CONTAINS:
          case ComboOperator.NOT_CONTAINS:
            if (!Array.isArray(value)) {
              throw new Error('Invalid value for array attribute');
            }
            const formattedValues = value.map((val) => field.choices.find((c) => c.value === val)?.displayName || val).join(', ');
            return `${displayName} ${inlineText} ${formattedValues} ${secondaryInlineText}`;
          case ComboOperator.IS_NULL:
          case ComparisonOperator.HAS_VALUE:
            return `${displayName} ${inlineText}`;
          default:
            throw new Error('Invalid comparison operator for array attribute');
        }
      }
      case FieldType.BOOLEAN: {
        const boolValue = value ? 'True' : 'False';
        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComboOperator.NOT_EQUALS:
            return `${displayName} ${inlineText} ${boolValue}`;
          case ComparisonOperator.HAS_VALUE:
          case ComboOperator.IS_NULL:
            return `${displayName} ${inlineText}`;
          default:
            console.warn('Unknown boolean operator, using fallback:', operator);
            return `${displayName} is ${boolValue}`;
        }
      }
      case FieldType.NUMBER:
      case FieldType.PERCENTAGE:
        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComparisonOperator.GREATER_THAN:
          case ComparisonOperator.GREATER_THAN_EQUALS:
          case ComparisonOperator.LESS_THAN:
          case ComparisonOperator.LESS_THAN_EQUALS:
          case ComboOperator.NOT_EQUALS:
            return `${displayName} ${inlineText} ${value}`;
          case ComboOperator.BETWEEN:
            return `${displayName} ${inlineText} ${value[0]} ${secondaryInlineText} ${value[1]}`;
          case ComboOperator.IS_NULL:
          case ComparisonOperator.HAS_VALUE:
            return `${displayName} ${inlineText}`;
          default:
            throw new Error('Invalid comparison operator for number attribute');
        }
      case FieldType.TEXT:
      case FieldType.EMAIL:
        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComparisonOperator.CONTAINS:
          case ComboOperator.NOT_CONTAINS:
          case ComboOperator.NOT_EQUALS:
            return `${displayName} ${inlineText} ${value} ${secondaryInlineText}`;
          case ComboOperator.IS_NULL:
          case ComparisonOperator.HAS_VALUE:
            return `${displayName} ${inlineText}`;
          default:
            throw new Error('Invalid comparison operator for text attribute');
        }
      default:
        console.warn('Unknown field type, using fallback display:', fieldType);
        // Fallback for unknown field types
        if (operator === ComboOperator.IS_NULL) {
          return `${displayName} is null`;
        } else if (operator === ComparisonOperator.HAS_VALUE) {
          return `${displayName} has value`;
        } else if (typeof value === 'boolean') {
          return `${displayName} is ${value ? 'True' : 'False'}`;
        } else if (value !== null && value !== undefined) {
          return `${displayName} ${inlineText || 'equals'} ${value}`;
        } else {
          return `${displayName} ${inlineText || 'condition'}`;
        }
    }
  };

  const handleNodeClick = () => {
    onNodeClick?.(nodeId);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsPopover(false);
    onDeleteNode?.(nodeId);
  };

  const handleOptionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsPopover(!showOptionsPopover);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open properties panel by selecting this node
    onNodeClick?.(nodeId);
  };

  const handleAddConditionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open properties panel to add condition
    onNodeClick?.(nodeId);
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowOptionsPopover(false);
      }
    };

    if (showOptionsPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showOptionsPopover]);

  // Support both V1 (metadata.children) and V2 (metadata.condition) formats
  const conditions = metadata?.children || [];
  const singleCondition = metadata?.condition;

  // Check if we have valid conditions - handle both regular conditions and LOGICAL conditions
  const hasValidConditions = conditions.some((c: any) =>
    (c?.attribute && c?.op) || // Regular condition
    (c?.type === 'LOGICAL' && c?.children?.length > 0) // LOGICAL condition with children
  ) || (singleCondition && (
    (singleCondition.attribute && singleCondition.op) || // Regular single condition
    (singleCondition.type === 'LOGICAL' && singleCondition.children?.length > 0) // LOGICAL single condition
  ));

  // if renderConditionTag would return "No added conditions.", treat as no conditions
  let actuallyHasConditions = hasValidConditions;
  if (actuallyHasConditions) {
    try {
      const testCondition = conditions.length > 0 ? conditions[0] : singleCondition;
      const rendered = testCondition ? renderConditionTag(testCondition) : '';
      if (rendered === 'No added conditions.') {
        actuallyHasConditions = false;
      }
    } catch {
      actuallyHasConditions = false;
    }
  }

  const hasConditions = actuallyHasConditions;

  // Get the primary condition to display
  const primaryCondition = conditions.length > 0 ? conditions[0] : singleCondition;

  // Layout logic using ReactFlow edge detection:
  const reactFlow = useReactFlow();

  // Get current edges from ReactFlow
  const edges = reactFlow.getEdges();

  // Check for child connections using ReactFlow edges
  const hasThenChild = edges.some((edge) => edge.source === nodeId && edge.sourceHandle === 'true');
  const hasElseChild = edges.some((edge) => edge.source === nodeId && edge.sourceHandle === 'false');

  // Layout based on child connections:
  // 1. No children: Both Then/Else at extreme positions
  // 2. Only Then child: Then centered, Else at right
  // 3. Only Else child: Else centered, Then at left
  // 4. Both children: Both at extreme positions
  const showBothBlocks = (!hasThenChild && !hasElseChild) || (hasThenChild && hasElseChild);
  const showCenteredThen = hasThenChild && !hasElseChild;
  const showCenteredElse = !hasThenChild && hasElseChild;

  return (
    <div className="relative">
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />

      {/* Main node container */}
      <div className="flex flex-col items-start justify-center relative w-[342px] transition-all duration-200">
        {/* Node with border */}
        <div className={cn(
          'w-full bg-white border border-gray-200 rounded-2xl transition-all duration-200',
          isSelected && 'border-black border-2'
        )}
        >
          {/* Header Card */}
          <div
            className="w-full bg-white cursor-pointer rounded-t-2xl"
            onClick={handleNodeClick}
          >
            <div className="flex h-12 items-center gap-1 p-3 relative w-full">
              <div className="flex justify-between flex-1 self-stretch grow items-center relative">
                <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
                  <div className="bg-pink-500 flex w-6 h-6 items-center justify-center relative rounded-lg">
                    <Shuffle className="w-4 h-4 text-white" />
                  </div>
                  <div className="relative w-fit font-medium text-gray-900 text-sm tracking-[0] leading-[22px] whitespace-nowrap">
                    Check if
                  </div>
                </div>
                <div className="inline-flex gap-2 flex-[0_0_auto] items-center relative">
                  <div ref={popoverRef}>
                    <Popover open={showOptionsPopover} onOpenChange={setShowOptionsPopover}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex flex-col w-6 h-6 items-center justify-center gap-2 px-2 py-0.5 bg-white rounded-lg hover:bg-gray-100"
                          onClick={handleOptionsClick}
                        >
                          <MoreVertical className="w-3 h-3 text-gray-600" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-1" align="end">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={handleDelete}
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete Node
                        </Button>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Separator line */}
          <div className="w-full h-px bg-gray-200" />

          {/* Content Card */}
          <div className="w-full bg-white rounded-b-2xl overflow-hidden">
            <div className="flex flex-col items-start gap-3 px-3 py-3 pb-10 relative w-full bg-white">
              {hasConditions ? (
                <div className="flex items-center justify-between px-3 py-2 relative self-stretch w-full flex-[0_0_auto] bg-gray-100 rounded-xl overflow-hidden">
                  <div className="text-sm font-medium text-gray-700">
                    {(() => {
                    try {
                      return renderConditionTag(primaryCondition);
                    } catch (error) {
                      return 'Condition configured';
                    }
                  })()}
                  </div>
                  {isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2 p-1.5 flex w-6 h-6 items-center justify-center relative rounded-lg hover:bg-gray-200"
                    onClick={handleEditClick}
                  >
                    <Edit2 className="w-3 h-3 text-gray-600" />
                  </Button>
                )}
                </div>
            ) : (
              <div className="flex items-center justify-between px-3 py-2 relative self-stretch w-full flex-[0_0_auto] bg-yellow-50 rounded-xl overflow-hidden">
                <Button
                  variant="outline"
                  className="inline-flex flex-col items-center gap-2 px-2 py-0.5 relative flex-[0_0_auto] bg-white rounded-lg border border-dashed border-gray-300 hover:bg-gray-50"
                  onClick={handleAddConditionClick}
                >
                  <div className="gap-2 inline-flex items-center relative flex-[0_0_auto]">
                    <Plus className="w-3 h-3 text-gray-600" />
                    <div className="relative w-fit mt-[-1.00px] font-medium text-gray-600 text-xs tracking-[0] leading-[1.5] whitespace-nowrap">
                      Add Condition
                    </div>
                  </div>
                </Button>
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Plus buttons positioned to overlap condition node border */}
        {isEditing && (
          <div className="relative w-full -mt-3 z-10">
            {showBothBlocks ? (
              // No children OR both children: Both at extreme positions
              <div className="flex justify-between items-center" style={{ paddingLeft: '15%', paddingRight: '15%' }}>
                <Button
                  size="sm"
                  className="flex w-6 h-6 items-center justify-center p-0 bg-green-600 rounded-full border-2 border-white hover:bg-green-700 shadow-md"
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    data.onShowOptionBar?.(nodeId, {
                      x: rect.left + rect.width / 2,
                      y: rect.top
                    }, false);
                  }}
                >
                  <Plus className="w-3 h-3 text-white" />
                </Button>
                <Button
                  size="sm"
                  className="flex w-6 h-6 items-center justify-center p-0 bg-red-600 rounded-full border-2 border-white hover:bg-red-700 shadow-md"
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    data.onShowOptionBar?.(nodeId, {
                      x: rect.left + rect.width / 2,
                      y: rect.top
                    }, true);
                  }}
                >
                  <Plus className="w-3 h-3 text-white" />
                </Button>
              </div>
            ) : showCenteredThen ? (
              // Only Then child: Then centered, Else at right
              <>
                <Button
                  size="sm"
                  className="absolute flex w-6 h-6 items-center justify-center p-0 bg-green-600 rounded-full border-2 border-white hover:bg-green-700 shadow-md"
                  style={{ left: '50%', transform: 'translateX(-50%)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    data.onShowOptionBar?.(nodeId, {
                      x: rect.left + rect.width / 2,
                      y: rect.top
                    }, false);
                  }}
                >
                  <Plus className="w-3 h-3 text-white" />
                </Button>
                <Button
                  size="sm"
                  className="absolute flex w-6 h-6 items-center justify-center p-0 bg-red-600 rounded-full border-2 border-white hover:bg-red-700 shadow-md"
                  style={{ left: '85%', transform: 'translateX(-50%)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    data.onShowOptionBar?.(nodeId, {
                      x: rect.left + rect.width / 2,
                      y: rect.top
                    }, true);
                  }}
                >
                  <Plus className="w-3 h-3 text-white" />
                </Button>
              </>
            ) : showCenteredElse ? (
              // Only Else child: Else centered, Then at left
              <>
                <Button
                  size="sm"
                  className="absolute flex w-6 h-6 items-center justify-center p-0 bg-green-600 rounded-full border-2 border-white hover:bg-green-700 shadow-md"
                  style={{ left: '15%', transform: 'translateX(-50%)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    data.onShowOptionBar?.(nodeId, {
                      x: rect.left + rect.width / 2,
                      y: rect.top
                    }, false);
                  }}
                >
                  <Plus className="w-3 h-3 text-white" />
                </Button>
                <Button
                  size="sm"
                  className="absolute flex w-6 h-6 items-center justify-center p-0 bg-red-600 rounded-full border-2 border-white hover:bg-red-700 shadow-md"
                  style={{ left: '50%', transform: 'translateX(-50%)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    data.onShowOptionBar?.(nodeId, {
                      x: rect.left + rect.width / 2,
                      y: rect.top
                    }, true);
                  }}
                >
                  <Plus className="w-3 h-3 text-white" />
                </Button>
              </>
            ) : null}
          </div>
        )}

        {/* Labels positioned above plus icons */}
        {isEditing && (
          <div className="relative w-full -mt-10 z-5">
            {showBothBlocks ? (
              // No children OR both children: Both labels at extreme positions
              <div className="flex justify-between items-center" style={{ paddingLeft: '15%', paddingRight: '15%' }}>
                <div className="text-xs text-gray-600 font-medium">Then</div>
                <div className="text-xs text-gray-600 font-medium">Else</div>
              </div>
            ) : showCenteredThen ? (
              // Only Then child: Then centered, Else at right
              <>
                <div className="absolute text-xs text-gray-600 font-medium" style={{ left: '50%', transform: 'translateX(-50%)' }}>
                  Then
                </div>
                <div className="absolute text-xs text-gray-600 font-medium" style={{ left: '85%', transform: 'translateX(-50%)' }}>
                  Else
                </div>
              </>
            ) : showCenteredElse ? (
              // Only Else child: Else centered, Then at left
              <>
                <div className="absolute text-xs text-gray-600 font-medium" style={{ left: '15%', transform: 'translateX(-50%)' }}>
                  Then
                </div>
                <div className="absolute text-xs text-gray-600 font-medium" style={{ left: '50%', transform: 'translateX(-50%)' }}>
                  Else
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Output handles positioned based on child connections */}
      {showBothBlocks ? (
        // No children OR both children: Both handles at extreme positions
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            id="true"
            isConnectable={false}
            className="w-3 h-3 bg-green-500 border-2 border-white"
            style={{ left: '15%' }}
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="false"
            isConnectable={false}
            className="w-3 h-3 bg-red-500 border-2 border-white"
            style={{ left: '85%' }}
          />
          {/* Add center handle for no children case (for End node connection) */}
          {!hasThenChild && !hasElseChild && (
            <Handle
              type="source"
              position={Position.Bottom}
              id="center"
              isConnectable={false}
              className="w-3 h-3 bg-gray-400 border-2 border-white"
              style={{ left: '50%' }}
            />
          )}
        </>
      ) : showCenteredThen ? (
        // Only Then child: Then handle centered, Else handle at right (faded)
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            id="true"
            isConnectable={false}
            className="w-3 h-3 bg-green-500 border-2 border-white"
            style={{ left: '50%' }}
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="false"
            isConnectable={false}
            className="w-3 h-3 bg-red-500 border-2 border-white opacity-30"
            style={{ left: '85%' }}
          />
        </>
      ) : showCenteredElse ? (
        // Only Else child: Else handle centered, Then handle at left (faded)
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            id="true"
            isConnectable={false}
            className="w-3 h-3 bg-green-500 border-2 border-white opacity-30"
            style={{ left: '15%' }}
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="false"
            isConnectable={false}
            className="w-3 h-3 bg-red-500 border-2 border-white"
            style={{ left: '50%' }}
          />
        </>
      ) : null}
    </div>
  );
};
